/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React from 'react'
import { List, ListItemButton, ListItemIcon, ListItemText, Popover } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Add, Download, NotificationsActive, Remove, Sync } from '@mui/icons-material'
import { OrderTableItem } from '../models/order'
import { getDifferenceFromNow } from 'utils/date-utils'
import { EXEMPT_ORDER_INVOICE_ID } from 'constants/orders'

// How long a line may sit in "Processing" before we treat it as stuck and offer a manual
// regenerate. Below this we still assume generation is genuinely in flight and only offer refresh.
const PROCESSING_STUCK_MINUTES = 5

type AdminOrdersActionsMenuProps = {
    anchorEl?: null | HTMLElement
    selectedRow?: OrderTableItem
    isCubitAdmin: boolean
    onClose: () => void
    onExemptAction: (id: string) => void
    onDownloadAction: (id: string) => void
    onRegenerateOrderAction: (id: string) => void
    onRegenerateAndCallbackAction: (id: string) => void
    onRefreshAction: () => void
}

export const AdminOrdersActionsMenu: React.FC<AdminOrdersActionsMenuProps> = ({
    anchorEl,
    isCubitAdmin,
    onExemptAction,
    onDownloadAction,
    onRegenerateOrderAction,
    onRegenerateAndCallbackAction,
    onRefreshAction,
    onClose,
    selectedRow,
}) => {
    const { t } = useTranslation()

    if (!selectedRow) return null

    const id = selectedRow.orderId

    const minutesDiff = selectedRow?.created
        ? getDifferenceFromNow(selectedRow.generated ?? selectedRow.created, 'minutes')
        : 0
    // getDifferenceFromNow is (date - now), so a line that started in the past yields a negative value.
    const minutesInProcessing = Math.abs(minutesDiff)

    return (
        <Popover
            open={true}
            anchorEl={anchorEl}
            anchorOrigin={{
                horizontal: 'left',
                vertical: 'bottom',
            }}
            onClose={onClose}
            slotProps={{
                paper: { sx: { minWidth: 200 } },
            }}
        >
            <List sx={{ width: '100%' }}>
                {selectedRow.status === 'Completed' && selectedRow.pdf && (
                    <ListItemButton onClick={() => onDownloadAction(id!)}>
                        <ListItemIcon>
                            <Download aria-label={t('TEXT_DOWNLOAD', 'Last ned')} />
                        </ListItemIcon>
                        <ListItemText primary={t('TEXT_DOWNLOAD', 'Last ned')} />
                    </ListItemButton>
                )}
                {isCubitAdmin && selectedRow.status === 'Failed' && (
                    <>
                        <ListItemButton onClick={() => onRegenerateOrderAction(id!)}>
                            <ListItemIcon>
                                <Sync aria-label={t('TEXT_REGENERATE', 'Generer på nytt')} />
                            </ListItemIcon>
                            <ListItemText primary={t('TEXT_REGENERATE', 'Generer på nytt')} />
                        </ListItemButton>
                        <ListItemButton onClick={() => onRegenerateAndCallbackAction(id!)}>
                            <ListItemIcon>
                                <NotificationsActive
                                    aria-label={t('TEXT_REGENERATE_AND_NOTIFY', 'Generer på nytt og varsle kunde')}
                                />
                            </ListItemIcon>
                            <ListItemText primary={t('TEXT_REGENERATE_AND_NOTIFY', 'Generer på nytt og varsle kunde')} />
                        </ListItemButton>
                    </>
                )}
                {isCubitAdmin && selectedRow.status === 'Processing' && minutesInProcessing > PROCESSING_STUCK_MINUTES && (
                    <>
                        <ListItemButton onClick={() => onRegenerateOrderAction(id!)}>
                            <ListItemIcon>
                                <Sync aria-label={t('TEXT_REGENERATE', 'Generer på nytt')} />
                            </ListItemIcon>
                            <ListItemText primary={t('TEXT_REGENERATE', 'Generer på nytt')} />
                        </ListItemButton>
                        <ListItemButton onClick={() => onRegenerateAndCallbackAction(id!)}>
                            <ListItemIcon>
                                <NotificationsActive
                                    aria-label={t('TEXT_REGENERATE_AND_NOTIFY', 'Generer på nytt og varsle kunde')}
                                />
                            </ListItemIcon>
                            <ListItemText primary={t('TEXT_REGENERATE_AND_NOTIFY', 'Generer på nytt og varsle kunde')} />
                        </ListItemButton>
                    </>
                )}
                {selectedRow.status === 'Processing' &&
                    minutesInProcessing > 1 &&
                    minutesInProcessing <= PROCESSING_STUCK_MINUTES && (
                        <ListItemButton onClick={onRefreshAction}>
                            <ListItemIcon>
                                <Download aria-label={t('TEXT_GENERATING', 'Genereres')} />
                            </ListItemIcon>
                            <ListItemText primary={t('TEXT_GENERATING', 'Genereres') + '...'} />
                        </ListItemButton>
                    )}
                {selectedRow.invoiceId === EXEMPT_ORDER_INVOICE_ID ? (
                    <ListItemButton onClick={() => onExemptAction(id!)}>
                        <ListItemIcon>
                            <Add aria-label={t('TEXT_UNDO_EXEMPT_ORDER', 'Angre fritatt')} color="success" />
                        </ListItemIcon>
                        <ListItemText primary={t('TEXT_UNDO_EXEMPT_ORDER', 'Angre fritatt')} />
                    </ListItemButton>
                ) : (
                    <ListItemButton onClick={() => onExemptAction(id!)}>
                        <ListItemIcon>
                            <Remove aria-label={t('TEXT_EXEMPT_ORDER', 'Unntatt')} color="error" />
                        </ListItemIcon>
                        <ListItemText primary={t('TEXT_EXEMPT_ORDER', 'Unntatt')} />
                    </ListItemButton>
                )}
            </List>
        </Popover>
    )
}
